// ─── Spec extraction seam (ocn-S816-P0 §C) ──────────────────────────────────
// ONE extraction contract the chat awaits when a subscriber uploads a spec:
// { summary_items: [{label, value, confidence}], open_questions: [] }.
//
// Today this is a THIN RESHAPE of the existing enrichment call the upload
// flow already uses (POST /wizard/app-idea/start-from-spec, consumed via
// subscriberChatApi.startWizardFromSpec) -- no new LLM pipeline. When
// ocn-S797's capability-manifest wiring PR lands, this module's internals
// are what get replaced: CapabilityManifest.capabilities + .confidence
// (apps/api/src/lib/factory/interview/types.ts) map 1:1 onto
// summary_items[].{label: capability, confidence}, and
// CapabilityManifest.vocabulary_gaps / any unresolved axis question map onto
// open_questions. Callers of buildSpecExtractionResult do not change.

import type { ConfirmationSummary, EnrichedRequirements } from '../hooks/useSubscriberChat.js';
import type { ParsedSpec } from '../api.js';

/** Mirrors apps/api/src/lib/factory/interview/types.ts's ConfidenceSource
 * (ocn-S797-P0) one-for-one so this seam accepts either producer without a
 * shape change: 'extracted' = pulled straight from the doc, 'asked' =
 * subscriber confirmed/typed it, 'derived' = computed from other answers,
 * 'defaulted' = a fallback guess (lowest confidence). */
export type SpecItemConfidence = 'extracted' | 'asked' | 'derived' | 'defaulted';

export interface SpecSummaryItem {
  label: string;
  value: string;
  confidence: SpecItemConfidence;
}

export interface SpecExtractionResult {
  summary_items: SpecSummaryItem[];
  open_questions: string[];
}

/**
 * Adapts a ConfirmationSummary/EnrichedRequirements pair (the existing
 * enrichment response) into the summary_items/open_questions contract.
 * Pure function -- no network, no randomness.
 */
export function buildSpecExtractionResult(
  summary: ConfirmationSummary | null | undefined,
  enriched?: EnrichedRequirements | null,
): SpecExtractionResult {
  const summary_items: SpecSummaryItem[] = [];
  const push = (label: string, value: string | null | undefined, confidence: SpecItemConfidence = 'extracted'): void => {
    if (value && value.trim()) summary_items.push({ label, value: value.trim(), confidence });
  };

  push('App name', summary?.productName);
  push('What it does', summary?.oneLineDescription);
  push('Business model', summary?.businessModel?.replace(/_/g, ' '));
  push('Primary user', summary?.primaryUserType);
  push('Architecture', summary?.archetypeHint, 'derived');
  for (const feature of summary?.coreFeatures ?? []) {
    push(`Feature: ${feature.name}`, feature.description || feature.name);
  }

  // Genuine gaps only: deterministic presence checks over the SAME response
  // already returned above -- never a second LLM call.
  const open_questions: string[] = [];
  if (!summary?.primaryUserType) open_questions.push('Who is the primary user of this app?');
  if (!summary?.coreFeatures || summary.coreFeatures.length === 0) {
    open_questions.push('What are the 3-5 core features?');
  }
  if (!enriched?.businessModel || enriched.businessModel === 'unknown') {
    open_questions.push('Is this a paid product, an internal tool, or something else?');
  }

  return { summary_items, open_questions };
}

/**
 * ocn-S818-P0 Part 1 ("I Have a Repo"): the cheap, no-analysis-pipeline ingest
 * for a freshly-connected repo. Reshapes what is trivially available (repo
 * name/description/language from the GitHub repo list already fetched for
 * the picker -- no new API call, no deep codebase scan) into the SAME
 * summary_items/open_questions contract buildSpecExtractionResult produces,
 * so the repo path and the spec path converge on one seam shape. Deep
 * codebase extraction (schema/routes/auth detection) does not exist yet --
 * every such fact is deliberately left as an open_question for the interview
 * rather than guessed. Pure function -- no network, no randomness.
 */
export function buildRepoIngestResult(repo: {
  name: string;
  fullName: string;
  description?: string | null;
  language?: string | null;
}): SpecExtractionResult {
  const summary_items: SpecSummaryItem[] = [];
  summary_items.push({ label: 'Repository', value: repo.fullName, confidence: 'extracted' });
  if (repo.language) summary_items.push({ label: 'Primary language', value: repo.language, confidence: 'extracted' });
  if (repo.description && repo.description.trim()) {
    summary_items.push({ label: 'Description', value: repo.description.trim(), confidence: 'extracted' });
  }

  // Genuine gaps: deep codebase extraction (framework/package manifest,
  // schema, routes, auth) is not built in this fire -- every such fact is an
  // honest open_question, never a guessed/defaulted value.
  // The framework and package manifest question is no longer asked: the subscriber
  // cannot be expected to know what the repo already says, and Sync reads it.
  const open_questions: string[] = [
    'What would you like to change or build next in this codebase?',
  ];
  if (!repo.description || !repo.description.trim()) {
    open_questions.push('What does this app do?');
  }

  return { summary_items, open_questions };
}

/**
 * Turns a parse-spec (ocn-S569, POST /wizard/parse-spec) result into plain
 * text so it can be fed through the SAME startWizardFromSpec seam as a
 * pasted/text-file spec -- one enrichment path, not a fork per file type.
 */
export function specDigestFromParsedSpec(result: ParsedSpec): string {
  const p = result.parsed;
  const lines: string[] = [];
  if (p.displayName || p.name) lines.push(`# ${p.displayName || p.name}`);
  if (p.description) lines.push(p.description);
  if (p.summary) lines.push(`\nSummary: ${p.summary}`);
  if (p.problemStatement) lines.push(`\nProblem: ${p.problemStatement}`);
  if (p.targetUsers) lines.push(`\nTarget users: ${p.targetUsers}`);
  if (p.coreFeatures) lines.push(`\nCore features: ${p.coreFeatures}`);
  if (p.keyFeatures?.length) lines.push(`\nKey features:\n${p.keyFeatures.map((f) => `- ${f}`).join('\n')}`);
  if (p.techStack) lines.push(`\nTech stack: ${p.techStack}`);
  if (p.techPreferences) lines.push(`\nTech preferences: ${p.techPreferences}`);
  if (p.deploymentTarget) lines.push(`\nDeployment: ${p.deploymentTarget}`);
  if (p.domain) lines.push(`\nDomain: ${p.domain}`);
  if (p.successCriteria) lines.push(`\nSuccess criteria: ${p.successCriteria}`);
  return lines.join('\n').trim();
}
